import { resetPendingBudgets } from './pendingBudgets';
import { resetNotifications } from './notifications';
import { resetOverdueAlert } from './overdueAlert';

export const isAuthenticated = () => localStorage.getItem('token') !== null;
export const getToken = () => localStorage.getItem('token');
export const setToken = token => localStorage.setItem('token', token);
export const setUser = user => localStorage.setItem('user', JSON.stringify(user) );
export const getUser = () => JSON.parse( localStorage.getItem('user') );
export const getPlan = () => JSON.parse( localStorage.getItem('plan') );
export const setPlan = plan => localStorage.setItem('plan', JSON.stringify(plan) );
export const setTheme = theme => localStorage.setItem('theme', JSON.stringify(theme) );
export const getTheme = () => JSON.parse( localStorage.getItem('theme') );
export const setData = data => localStorage.setItem('data', JSON.stringify(data) );
export const getData = () => JSON.parse( localStorage.getItem('data') );

export const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    resetPendingBudgets();
    resetNotifications();
    resetOverdueAlert();
};

export const hasPermission = permissionsName => {
    permissionsName = typeof permissionsName === 'string' || permissionsName instanceof String ? [permissionsName] : permissionsName;
    
    let has = false;
    let user = getUser();
    let permissions = user?.role?.permissions ? user.role.permissions : [];
    permissions.map(permission => {
        if(permissionsName.includes(permission.name)){
            has = true;
        }
    });

    return has;
}

export const hasPlanFeature = featureValue => {
    let has = false;
    let plan = getPlan();
    let features = plan?.features ?? [];

    features.map(feature => {
        if(feature.value === featureValue){
            has = true;
        }
    });
    
    return has;
}

export const FEATURE_ENABLED = 'enabled';
export const FEATURE_COMING_SOON = 'coming_soon';
export const FEATURE_LOCKED = 'locked';

/**
 * Estado da feature no plano (BDM-1315): liberada, anunciada como "em breve"
 * ou fora do plano (cadeado e convite para contratar).
 */
export const getFeatureState = featureValue => {
    if (hasPlanFeature(featureValue)) return FEATURE_ENABLED;

    const upcoming = getPlan()?.upcoming_features ?? [];

    return upcoming.some(feature => feature.value === featureValue) ? FEATURE_COMING_SOON : FEATURE_LOCKED;
};

export const PLAN_CHANGED_EVENT = 'brindeme:plan-changed';

const planFeatureSignature = plan => JSON.stringify([
    (plan?.features ?? []).map(feature => feature.value).sort(),
    (plan?.upcoming_features ?? []).map(feature => feature.value).sort(),
]);

/**
 * Grava o plano reconferido no backend e avisa o painel quando as features
 * mudaram: o que já está na tela foi desenhado com o plano antigo do login.
 */
export const refreshPlan = plan => {
    const changed = planFeatureSignature(getPlan()) !== planFeatureSignature(plan);

    setPlan(plan);

    if (changed) {
        window.dispatchEvent(new Event(PLAN_CHANGED_EVENT));
    }
};

export const GESTAO_FEATURE = 'gestao';

export const GESTAO_PERMISSIONS = ['view_finance', 'manage_finance', 'view_fiscal', 'manage_fiscal', 'view_purchase_order', 'manage_purchase_order'];

export const canUseGestao = () => hasPlanFeature(GESTAO_FEATURE);

export const getGestaoState = () => getFeatureState(GESTAO_FEATURE);

export const canSeeStock = () => hasPlanFeature('stock');

export const canUseProductMock = () => hasPlanFeature('product_mock');

export const canUseOpenFinance = () => hasPlanFeature('open_finance');

export const canSeeProductCost = () => hasPermission('show_product_supplier_price_in_budget');

export const canSeeProductSupplier = () => hasPermission('show_product_supplier_in_budget');

export const canSeeCustomizationCost = () => hasPermission('show_customization_supplier_price_in_budget');

export const canSeeCustomizationSupplier = () => hasPermission('show_customization_supplier_in_budget');

export const hasPlanTheme = themeId => {
    const plan = getPlan();
    const themes = plan?.themes ?? [];
    if (themes.length === 0) return true;
    return themes.some(t => String(t.id) === String(themeId));
};

// Marcas é recurso de plano (feature `brands`) e tem permissões próprias, então
// todo ponto de marca depende das duas coisas. Os pontos espalhados pelo sistema
// (campo e filtro no produto, select no orçamento/empresa/pessoa, "Vendo como"
// nas categorias) usam canSeeBrands. O backend recusa a escrita de quem não tem
// a feature, e leitura continua aberta — por isso canUseBrands existe sozinho.
export const canUseBrands = () => hasPlanFeature('brands');

export const canSeeBrands = () => canUseBrands() && hasPermission('view_brand');

export const canCreateBrands = () => canUseBrands() && hasPermission('create_brand');

export const canEditBrands = () => canUseBrands() && hasPermission('edit_brand');

export const canDeleteBrands = () => canUseBrands() && hasPermission('delete_brand');

export const hasQuoteDeletePermission = quote => {
    if(quote === null) return false;
    if(quote.order !== null) return hasPermission('delete_budget_with_order');
    if(quote.proposals.length > 0) return hasPermission('delete_budget_with_proposal');
    return hasPermission('delete_budget_without_proposal');
}

export const hasSupportTeamDomain = () => {
    const domains = ["@brinde.me"];
    const user = getUser();

    if (!user?.email) return false;

    return domains.some(domain => user.email.toLowerCase().endsWith(domain.toLowerCase()));
}

export default isAuthenticated;